'use client';

import { useEffect, useRef, useSyncExternalStore } from 'react';
import { usePathname } from 'next/navigation';

/** Max time to keep the shell on a pending skeleton if the route never commits. */
const PENDING_NAV_TIMEOUT_MS = 4_000;

let pendingHref: string | null = null;
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((listener) => listener());
}

/** Instant navigation target — cleared when the route catches up. */
export function setPendingHref(href: string | null) {
  if (pendingHref === href) return;
  pendingHref = href;
  emit();
}

function pathOnly(href: string): string {
  return href.split('?')[0]?.split('#')[0] ?? href;
}

/**
 * True when `path` is `root`, or a nested route under `root`.
 * Workspace-root paths (`/` or `/{workspaceId}`) match exactly — otherwise
 * every page under the workspace would count as Home.
 */
export function isPathOrDescendant(path: string | null, root: string): boolean {
  if (!path) return false;
  const pathHref = pathOnly(path);
  const rootHref = pathOnly(root);
  if (pathHref === rootHref) return true;
  const rootSegments = rootHref.split('/').filter(Boolean);
  if (rootSegments.length <= 1) return false;
  return pathHref.startsWith(`${rootHref}/`);
}

/**
 * Mark an outbound soft navigation so the content pane can swap to a skeleton
 * on the same frame as the click. No-ops when already on that exact path.
 */
export function markPendingNavigation(href: string, currentPathname?: string | null) {
  if (!href || href.startsWith('http') || href.startsWith('mailto:')) return;
  const hrefPath = pathOnly(href);
  if (currentPathname === hrefPath) return;
  setPendingHref(hrefPath);
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot() {
  return pendingHref;
}

function getServerSnapshot() {
  return null;
}

function isPendingResolved(pathname: string | null, pending: string) {
  return isPathOrDescendant(pathname, pending);
}

/** Instant nav target — cleared when the route catches up, or on recovery. */
export function usePendingHref() {
  const pathname = usePathname();
  const pending = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const prevPathnameRef = useRef(pathname);

  useEffect(() => {
    const prevPathname = prevPathnameRef.current;
    prevPathnameRef.current = pathname;

    // Successful navigation (or any other committed route change).
    // Skip the initial mount so remounts (e.g. mobile sheet) don't drop an in-flight pending.
    if (prevPathname !== pathname) {
      setPendingHref(null);
      return;
    }

    if (!pending) return;

    // Already on the destination (e.g. race with a concurrent update).
    if (isPendingResolved(pathname, pending)) {
      setPendingHref(null);
      return;
    }

    // Bounded recovery: cancelled / failed navigations never change pathname.
    const timeoutId = window.setTimeout(() => {
      setPendingHref(null);
    }, PENDING_NAV_TIMEOUT_MS);

    return () => window.clearTimeout(timeoutId);
  }, [pending, pathname]);

  return pending;
}
